import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import { DialogContentWithCards } from '../components/DialogContent';
import { NewDialogTitle } from '../components/NewDialogTitle';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { PaperCard } from '../components/PaperCard';
import { SelectInput } from '../components/SelectInput';
import SelectOrganization from '../containers/SelectOrganization';
import MaterialTable from 'material-table';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import Checkbox from '@material-ui/core/Checkbox';
import VisibilityOnIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import GetAppIcon from '@material-ui/icons/GetApp';
import Tooltip from '@material-ui/core/Tooltip';
import FormControl from '@material-ui/core/FormControl';
import NativeSelect from '@material-ui/core/NativeSelect';
import _ from 'lodash';
import MultiInputTextField from '../components/MultiInputTextField';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { languageOptions, timeZoneOptions } from '../utils/deviceGroupOptions';
import { db } from '../firebase/firebase';
import { NewTextInput } from '../components/NewTextInput';
import { currentUserIsRole, getCurrentUserOrganizationRef } from '../redux/actions/utils';
import { saveDeviceGroup } from '../redux/actions/deviceGroupActions';
import { generateRegistrationCode } from '../utils/deviceGroupUtils';
import { ShowHide } from '../components/ShowHide';
import FormControlLabel from '@material-ui/core/FormControlLabel';

const NONE = 'NONE';
const UNMANAGED = 'UNMANAGED';
const AUTO = 'AUTO';
const USER_INITIATED = 'USER_INITIATED';
const FORCE_ON_BOOT = 'FORCE_ON_BOOT';
const REQUEST_ON_BOOT = 'REQUEST_ON_BOOT';

const UNMANAGED_SELECTION = { value: UNMANAGED, label: <em>Unmanaged</em> };
const NONE_SELECTION = { value: NONE, label: <em>None</em> };
const AUTO_SELECTION = { value: AUTO, label: <em>Auto</em> };

const systemUpdateTypeOptions = [
    { value: USER_INITIATED, label: 'User Initiated (Requires Mighty Launcher)' },
    { value: REQUEST_ON_BOOT, label: 'If Available, Request System Update On Boot' },
    { value: FORCE_ON_BOOT, label: 'If Available, Force System Update On Boot' },
];

const skinnyTableHeader = {
    paddingRight: '0px',
    width: '1px',
};

const getDeviceGroupApprovedAppIds = ({ deviceGroup: { contentBundleRefs, approvedApps }, contentBundles }) => {
    let allApprovedApps = [];
    if (contentBundleRefs)
        contentBundleRefs.map((bundle) => {
            if (!contentBundles[bundle.id]) return;
            contentBundles[bundle.id].appRefs.map(({ id }) => allApprovedApps.push(id));
        });
    if (approvedApps) approvedApps.map(({ id }) => allApprovedApps.push(id));
    return allApprovedApps;
};

export const NewCRUDDeviceGroupDialog = ({ deviceGroupId, onClose, onDelete }) => {
    let deviceGroup = useSelector((state) => state.data.deviceGroups[deviceGroupId]);
    const deviceSystemVersions = useSelector((state) => state.data.deviceSystemVersions);
    const apps = useSelector((state) => state.data.apps);
    const contentBundles = useSelector((state) => state.data.contentBundles);
    const hasInitializedDbData = useSelector((state) => state.data.hasInitializedDbData);
    let [deviceGroupEdits, setDeviceGroupEdits] = useState({});

    if ((deviceGroupId && !deviceGroup) || !hasInitializedDbData) return <></>; // wait until everything is loaded

    let currentDeviceGroup = { ...deviceGroup, ...deviceGroupEdits };
    // console.log(currentDeviceGroup, deviceGroupEdits);
    let updateDeviceGroupEdits = function(changes) {
        if (currentUserIsRole('User')) return;
        deviceGroupEdits = { ...deviceGroupEdits, ...changes }; // need to set it here too for batched updates
        setDeviceGroupEdits(deviceGroupEdits);
    };

    // set default values if we're creating a new device group
    if (!deviceGroupId) {
        deviceGroup = {};
        if (_.isEmpty(deviceGroupEdits)) {
            setDeviceGroupEdits({
                organizationRef: getCurrentUserOrganizationRef(),
                registrationCode: generateRegistrationCode(),
            });
        }
    }

    let allApprovedAppIds = getDeviceGroupApprovedAppIds({ deviceGroup: currentDeviceGroup, contentBundles });

    let hiddenApps = _.map(currentDeviceGroup.hiddenAppRefs, (ref) => ref.id);

    let forcedApps = _.map(currentDeviceGroup.forcedAppRefs, (ref) => ref.id);

    let onKioskAppChange = (kioskAppId) => updateDeviceGroupEdits({ kioskAppRef: kioskAppId === NONE ? null : db.collection('apps').doc(kioskAppId) });

    // Used to update any deviceGroup list of appReferences. i.e: 'approvesApps' and 'hiddenAppRefs'.
    let onAppListFieldChange = ({ fieldName, appId, value }) => {
        let appList = deviceGroupEdits[fieldName];
        if (!appList) {
            appList = deviceGroup[fieldName] || [];
            appList = [...appList];
        }
        if (value) {
            if (!_.find(appList, { id: appId })) {
                appList.push(db.collection('apps').doc(appId));
            }
        } else {
            _.remove(appList, { id: appId });
        }

        updateDeviceGroupEdits({ [fieldName]: appList });
    };

    let onMapFieldChange = ({ fieldName, key, value }) => {
        let map = deviceGroupEdits[fieldName];
        if (!map) {
            map = deviceGroup[fieldName] || {};
            map = { ...map };
        }
        if (value === UNMANAGED) {
            delete map[key];
        } else {
            map[key] = value;
        }
        updateDeviceGroupEdits({ [fieldName]: map });
    };

    let onSave = () => {
        saveDeviceGroup({ deviceGroupId, changes: deviceGroupEdits });
        onClose();
    };

    let kioskAppKey = _.get(currentDeviceGroup, 'kioskAppRef.id') || NONE;
    let firstTimeUseAppKey = _.get(currentDeviceGroup, 'firstTimeUseAppRef.id') || NONE;

    let selectableAppOptions = allApprovedAppIds.map((appId) => {
        let app = apps[appId];
        return { value: appId, label: app ? app.title : 'null' };
    });
    selectableAppOptions = [NONE_SELECTION, ...selectableAppOptions];

    let systemLanguageOptions = languageOptions.map((option) => {
        return { value: `${option.language}-${option.country}`, label: option.label };
    });
    systemLanguageOptions = [UNMANAGED_SELECTION, ...systemLanguageOptions];

    let systemTimeZoneOptions = timeZoneOptions.map((option) => {
        return { value: option.id, label: `GMT${option.offset < 0 ? '' : '+'}${option.offset} ${option.name}` };
    });
    systemTimeZoneOptions = [UNMANAGED_SELECTION, AUTO_SELECTION, ...systemTimeZoneOptions];

    let picoNeo2SystemVersionOptions = _.filter(deviceSystemVersions, (version) => version.model === 'Pico Neo 2').map((version) => {
        return { value: version._id, label: `PUI: ${version.version}` };
    });
    picoNeo2SystemVersionOptions = [UNMANAGED_SELECTION, ...picoNeo2SystemVersionOptions];

    let picoG24KSystemVersionOptions = _.filter(deviceSystemVersions, (version) => version.model === 'Pico G2 4K').map((version) => {
        return { value: version._id, label: `PUI: ${version.version}` };
    });
    picoG24KSystemVersionOptions = [UNMANAGED_SELECTION, ...picoG24KSystemVersionOptions];

    let getAppSelectionValid = (appId) => {
        return allApprovedAppIds.indexOf(appId) != -1 || appId === NONE;
    };

    let getTableData = () => {
        let data = Object.values(apps);
        data = _.groupBy(data, (app) => {
            return app.packageName + app.organizationRef.id + (!!app.apkUrl ? 'true' : 'false'); // group by packageName and org and managed/external
        });
        data = _.map(Object.values(data), (appVersions) => {
            appVersions = _.orderBy(appVersions, ['latestVersion', 'versionName'], ['desc', 'desc']);
            let primaryApp = null;
            let defaultVersionClone = null;
            for (let i = 0; i < appVersions.length; i++) {
                let appVersion = appVersions[i];
                if (appVersion.defaultVersionClone) {
                    defaultVersionClone = appVersion;
                    if (!primaryApp) primaryApp = appVersion;
                }
                if (allApprovedAppIds.indexOf(appVersion._id) != -1) primaryApp = appVersion;
            }
            if (!primaryApp) primaryApp = appVersions[0];
            if (!primaryApp.tableData) primaryApp.tableData = {};
            primaryApp.tableData.appVersions = appVersions;
            primaryApp.tableData.defaultVersionClone = defaultVersionClone;
            return primaryApp;
        });
        let approvedAppObjs = _.map(deviceGroup.approvedApps, (appRef) => apps[appRef.id] || {});

        return _.sortBy(data, (a) => !_.find(approvedAppObjs, { packageName: a.packageName }), (a) => !a.apkUrl, 'packageName');
    };

    let columns = [
        {
            title: 'Approved',
            headerStyle: skinnyTableHeader,
            render: (rowData) => {
                let checked = allApprovedAppIds.indexOf(rowData._id) != -1;
                let title = checked ? 'App Approved' : 'App Not Approved';
                return (
                    <Tooltip title={title} aria-label={title}>
                        <Checkbox
                            checked={checked}
                            onChange={(event) => {
                                ReactDOM.unstable_batchedUpdates(() => {
                                    if (!event.target.checked) {
                                        onAppListFieldChange({ fieldName: 'hiddenAppRefs', appId: rowData._id, value: false });
                                        onAppListFieldChange({ fieldName: 'forcedAppRefs', appId: rowData._id, value: false });
                                    }
                                    onAppListFieldChange({ fieldName: 'approvedApps', appId: rowData._id, value: event.target.checked });
                                });
                            }}
                        />
                    </Tooltip>
                );
            },
            customSort: (rowDataA, rowDataB) => {
                let selectedA = allApprovedAppIds.indexOf(rowDataA._id) != -1;
                let selectedB = allApprovedAppIds.indexOf(rowDataB._id) != -1;
                return selectedA === selectedB ? 0 : selectedA ? -1 : 1;
            },
        },
        {
            title: '',
            sorting: false,
            headerStyle: skinnyTableHeader,
            cellStyle: { padding: '0px' },
            render: (rowData) => {
                let disabled = allApprovedAppIds.indexOf(rowData._id) === -1;
                let checked = hiddenApps.indexOf(rowData._id) != -1 || !apps[rowData._id].showInLaunchers;
                let title = checked ? 'Show App in Mighty Launcher' : 'Hide App in Mighty Launcher';
                return (
                    <Tooltip title={title} aria-label={title}>
                        <Checkbox
                            checked={checked}
                            disabled={disabled || !apps[rowData._id].showInLaunchers}
                            style={disabled ? { display: 'none' } : {}}
                            onChange={(event) => onAppListFieldChange({ fieldName: 'hiddenAppRefs', appId: rowData._id, value: event.target.checked })}
                            icon={<VisibilityOnIcon />}
                            checkedIcon={<VisibilityOffIcon />}
                        />
                    </Tooltip>
                );
            },
        },
        {
            title: '',
            sorting: false,
            headerStyle: skinnyTableHeader,
            cellStyle: { padding: '0px' },
            render: (rowData) => {
                let disabled = allApprovedAppIds.indexOf(rowData._id) === -1;
                let checked = forcedApps.indexOf(rowData._id) != -1;
                let title = checked
                    ? 'Force Install App (App will be uninstalled before installing a new version)'
                    : 'ADVANCED FEATURE - Uninstall app before installing a new version. Only enable this if you want to rollback an app and clear app preferences/data in between app updates.';
                return (
                    <Tooltip title={title} aria-label={title}>
                        <Checkbox
                            checked={checked}
                            disabled={disabled || !apps[rowData._id].showInLaunchers}
                            style={disabled ? { display: 'none' } : {}}
                            onChange={(event) => onAppListFieldChange({ fieldName: 'forcedAppRefs', appId: rowData._id, value: event.target.checked })}
                            icon={<GetAppIcon />}
                            checkedIcon={<GetAppIcon />}
                        />
                    </Tooltip>
                );
            },
        },
        {
            title: 'Version',
            render: (rowData) => {
                if (!rowData.apkUrl) return '';
                return (
                    <FormControl style={{ width: '100%' }}>
                        <NativeSelect
                            value={rowData._id}
                            onChange={(event) => {
                                ReactDOM.unstable_batchedUpdates(() => {
                                    let oldAppVersionId = rowData._id;
                                    let newAppVersionId = event.target.value;

                                    if (oldAppVersionId !== newAppVersionId) {
                                        if (kioskAppKey === oldAppVersionId) {
                                            onKioskAppChange(newAppVersionId);
                                        }
                                        if (
                                            _.find(currentDeviceGroup.hiddenAppRefs, (appRef) => {
                                                return appRef.id === oldAppVersionId;
                                            })
                                        ) {
                                            onAppListFieldChange({ fieldName: 'hiddenAppRefs', appId: oldAppVersionId, value: false });
                                            onAppListFieldChange({ fieldName: 'hiddenAppRefs', appId: newAppVersionId, value: true });
                                        }
                                        if (
                                            _.find(currentDeviceGroup.forcedAppRefs, (appRef) => {
                                                return appRef.id === oldAppVersionId;
                                            })
                                        ) {
                                            onAppListFieldChange({ fieldName: 'forcedAppRefs', appId: oldAppVersionId, value: false });
                                            onAppListFieldChange({ fieldName: 'forcedAppRefs', appId: newAppVersionId, value: true });
                                        }
                                        onAppListFieldChange({ fieldName: 'approvedApps', appId: oldAppVersionId, value: false });
                                        onAppListFieldChange({ fieldName: 'approvedApps', appId: newAppVersionId, value: true });
                                    }
                                });
                            }}
                            inputProps={{
                                id: 'version-native-helper',
                            }}
                        >
                            {rowData.tableData.defaultVersionClone ? <option value={rowData.tableData.defaultVersionClone._id}>Default</option> : ''}
                            {rowData.tableData.appVersions.map((appVersion) => {
                                if (appVersion.defaultVersionClone) return '';
                                return (
                                    <option key={appVersion._id} value={appVersion._id}>
                                        {appVersion.latestVersion}
                                        {appVersion.versionName ? ` (${appVersion.versionName})` : ''}
                                        {appVersion.defaultVersion ? ` [Current Default]` : ''}
                                    </option>
                                );
                            })}
                        </NativeSelect>
                    </FormControl>
                );
            },
        },
        {
            title: 'Icon',
            sorting: false,
            render: (rowData) => <img src={rowData.iconUrl} alt={rowData.title} style={{ width: '50px' }} />,
        },
        {
            title: 'Title',
            field: 'title',
        },
        {
            title: 'Package Name',
            field: 'packageName',
        },
        {
            title: 'App Type',
            // defaultSort: 'desc',
            render: (rowData) => (rowData.apkUrl ? 'Managed' : 'External'),
            customSort: (rowDataA, rowDataB) => {
                let typeA = rowDataA.apkUrl ? 'Managed' : 'External';
                let typeB = rowDataB.apkUrl ? 'Managed' : 'External';
                return typeA.localeCompare(typeB);
            },
        },
        {
            title: 'Version Code',
            field: 'latestVersion',
        },
        {
            title: 'Version Name',
            field: 'versionName',
        },
    ];

    // TODO dont allow editting if user is not allowed to edit.
    return (
        <Dialog maxWidth="lg" scroll="body" onClose={onClose} open>
            <NewDialogTitle
                onCancel={onClose}
                onSave={onSave}
                onDelete={onDelete}
                organizationRef={currentDeviceGroup.organizationRef}
                globalAccess={currentDeviceGroup.globalAccess}
                showSaveButton={!_.isEmpty(deviceGroupEdits)}
                saveAvaliable={
                    !_.isEmpty(deviceGroupEdits) && getAppSelectionValid(kioskAppKey) && getAppSelectionValid(firstTimeUseAppKey) && currentDeviceGroup.name && currentDeviceGroup.registrationCode
                }
                darkBg
            >
                {deviceGroup.name ? `Device Group: ${deviceGroup.name}` : 'New Device Group'}
            </NewDialogTitle>
            <DialogContentWithCards>
                <Typography variant="caption">* Required Inputs</Typography>

                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <PaperCard>
                            <NewTextInput label={'Name'} value={currentDeviceGroup.name || ''} onChange={(name) => updateDeviceGroupEdits({ name })} required />
                            <NewTextInput
                                label={'Registration Code'}
                                value={currentDeviceGroup.registrationCode}
                                onChange={(registrationCode) => updateDeviceGroupEdits({ registrationCode })}
                                required
                            />
                            {/* <Chips
                data={allAppBundles}
                onChange={onBundleSelect}
                selectedValue={selectedBundleKeys}
                title="Approved App Bundles"
              /> */}
                            <SelectInput
                                value={kioskAppKey || NONE}
                                options={selectableAppOptions}
                                onChange={onKioskAppChange}
                                label={getAppSelectionValid(kioskAppKey) ? 'Kiosk App' : 'Not a valid Kiosk App selection'}
                                error={!getAppSelectionValid(kioskAppKey)}
                            />

                            <SelectOrganization value={currentDeviceGroup._organizationId} onChange={() => {}} />
                        </PaperCard>
                    </Grid>

                    <Grid container item spacing={3} xs={6} alignContent="flex-start">
                        <Grid item xs={12}>
                            <PaperCard>
                                <Typography variant="body1">There are two ways to add new devices to this group:</Typography>
                                <Typography variant="body2" component="span">
                                    <ol>
                                        <li>Register a new device using the Registration Code.</li>
                                        <li>Move an existing device to this group from the Devices page.</li>
                                    </ol>
                                </Typography>
                            </PaperCard>
                        </Grid>
                        <Grid item xs={12}>
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>System Settings (Advanced)</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div style={{ width: '100%' }}>
                                        {/* <FormControlLabel
                      control={
                        <Checkbox
                          checked={_.get(currentDeviceGroup, 'androidUserRestrictions.no_debugging_features') || false}
                          onChange={(event) => onMapFieldChange({ fieldName: 'androidUserRestrictions', key: 'no_debugging_features', value: event.target.checked })}
                        />
                      }
                      label="Disable USB Debugging Features"
                    /> */}
                                        <SelectInput
                                            value={_.get(currentDeviceGroup, 'androidDeviceGlobalSettings.adb_enabled') || UNMANAGED}
                                            options={[UNMANAGED_SELECTION, { value: '0', label: 'Disabled (Recommended For Production Deployments)' }, { value: '1', label: 'Enabled' }]}
                                            onChange={(adbEnabled) => onMapFieldChange({ fieldName: 'androidDeviceGlobalSettings', key: 'adb_enabled', value: adbEnabled })}
                                            label="USB Debugging (ADB)"
                                        />
                                    </div>
                                </AccordionDetails>
                            </Accordion>
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>Pico Settings (Advanced)</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div style={{ width: '100%' }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={_.get(currentDeviceGroup, 'androidUserRestrictions.no_factory_reset') || false}
                                                    onChange={(event) =>
                                                        onMapFieldChange({ fieldName: 'androidUserRestrictions', key: 'no_factory_reset', value: event.target.checked ? true : UNMANAGED })
                                                    }
                                                />
                                            }
                                            label="Disable Factory Reset"
                                        />
                                        <SelectInput
                                            value={
                                                currentDeviceGroup.targetDeviceSystemVersions && currentDeviceGroup.targetDeviceSystemVersions['Pico Neo 2']
                                                    ? currentDeviceGroup.targetDeviceSystemVersions['Pico Neo 2'].id
                                                    : UNMANAGED
                                            }
                                            options={picoNeo2SystemVersionOptions}
                                            onChange={(systemVersionId) => {
                                                let newValue = systemVersionId === UNMANAGED ? null : db.collection('deviceSystemVersions').doc(systemVersionId);
                                                onMapFieldChange({ fieldName: 'targetDeviceSystemVersions', key: 'Pico Neo 2', value: newValue });
                                            }}
                                            label="Pico Neo 2: Target System PUI Version"
                                        />
                                        <SelectInput
                                            value={
                                                currentDeviceGroup.targetDeviceSystemVersions && currentDeviceGroup.targetDeviceSystemVersions['Pico G2 4K']
                                                    ? currentDeviceGroup.targetDeviceSystemVersions['Pico G2 4K'].id
                                                    : UNMANAGED
                                            }
                                            options={picoG24KSystemVersionOptions}
                                            onChange={(systemVersionId) => {
                                                let newValue = systemVersionId === UNMANAGED ? null : db.collection('deviceSystemVersions').doc(systemVersionId);
                                                onMapFieldChange({ fieldName: 'targetDeviceSystemVersions', key: 'Pico G2 4K', value: newValue });
                                            }}
                                            label="Pico G2 4K: Target System PUI Version"
                                        />
                                        <SelectInput
                                            value={currentDeviceGroup.systemUpdateType || USER_INITIATED}
                                            options={systemUpdateTypeOptions}
                                            onChange={(systemUpdateType) => updateDeviceGroupEdits({ systemUpdateType })}
                                            label="System Update Type (Requires Target System PUI Version)"
                                        />
                                        <SelectInput
                                            value={currentDeviceGroup.localeLanguageTag || UNMANAGED}
                                            options={systemLanguageOptions}
                                            onChange={(localeLanguageTag) => updateDeviceGroupEdits({ localeLanguageTag })}
                                            label="System Language"
                                        />
                                        <SelectInput
                                            value={currentDeviceGroup.timeZoneSetting || UNMANAGED}
                                            options={systemTimeZoneOptions}
                                            onChange={(timeZoneSetting) => updateDeviceGroupEdits({ timeZoneSetting })}
                                            label="System Time Zone"
                                        />
                                    </div>
                                </AccordionDetails>
                            </Accordion>
                            {/* <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography>Mighty Launcher Settings (Advanced)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <div style={{ width: '100%' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!_.get(currentDeviceGroup, 'customLauncherSettings.guardianSetupOnLaunch')}
                          onChange={(event) => onMapFieldChange({ fieldName: 'customLauncherSettings', key: 'guardianSetupOnLaunch', value: event.target.checked })}
                        />
                      }
                      label="Open Guardian Setup On Launch (Pico Headsets Only)"
                    />
                    { <NewTextInput
                      label={"Admin PIN"}
                      value={_.get(currentDeviceGroup, 'customLauncherSettings.adminPin') || '5112'}
                      onChange={(val) => onMapFieldChange({ fieldName: 'customLauncherSettings', key: 'adminPin', value: val })}
                      required
                    /> }
                  </div>
                </AccordionDetails>
              </Accordion> */}
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography>First Time Use Settings (Advanced)</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div style={{ width: '100%' }}>
                                        <SelectInput
                                            value={firstTimeUseAppKey}
                                            options={selectableAppOptions}
                                            onChange={(firstTimeUseAppId) =>
                                                updateDeviceGroupEdits({ firstTimeUseAppRef: firstTimeUseAppId === NONE ? null : db.collection('apps').doc(firstTimeUseAppId) })
                                            }
                                            label={getAppSelectionValid(firstTimeUseAppKey) ? 'First Time Use App' : 'Not a valid First Time Use App selection'}
                                            error={!getAppSelectionValid(firstTimeUseAppKey)}
                                        />
                                        <MultiInputTextField
                                            initData={currentDeviceGroup.firstTimeUseFilePaths}
                                            onDataChange={(firstTimeUseFilePaths) => {
                                                firstTimeUseFilePaths = firstTimeUseFilePaths.filter(function(el) {
                                                    return el.length; // remove empty values
                                                });
                                                updateDeviceGroupEdits({ firstTimeUseFilePaths });
                                            }}
                                        />
                                    </div>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <MaterialTable
                            key={'default'}
                            title="Approved Apps"
                            columns={columns}
                            data={getTableData()}
                            icons={{
                                SortArrow: ArrowUpwardIcon,
                            }}
                            options={{
                                sorting: true,
                                pageSize: 25,
                                pageSizeOptions: [5, 10, 25, 100],
                                padding: 'dense',
                                actionsColumnIndex: -1,
                                debounceInterval: 500,
                                toolbarButtonAlignment: 'right',
                                draggable: false,
                                emptyRowsWhenPaging: false,
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContentWithCards>
        </Dialog>
    );
};
